import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import { motion, useReducedMotion } from 'framer-motion';

import {
    getTrackedOrderDisplayStatus,
    getTrackedOrders,
    refreshTrackedOrderStatuses,
} from '../utils/lastOrder';

const MENU_EASE = [0.22, 1, 0.36, 1];

const menuFadeUp = (
    delay = 0,
    prefersReducedMotion = false,
    canAnimate = false
) => {
    const hidden = { opacity: 0, y: 18 };
    const visible = { opacity: 1, y: 0 };

    if (prefersReducedMotion) {
        return {
            initial: false,
            animate: visible,
            transition: { duration: 0 }
        };
    }

    return {
        initial: hidden,
        animate: canAnimate ? visible : hidden,
        transition: {
            duration: 0.48,
            delay: canAnimate ? delay : 0,
            ease: MENU_EASE
        }
    };
};

function TrackedOrders({
    outletId,
    canAnimate = false
}) {
    const navigate = useNavigate();
    const prefersReducedMotion = useReducedMotion();

    const [trackedOrders, setTrackedOrders] = useState(
        () => getTrackedOrders(outletId)
    );

    useEffect(() => {
        let cancelled = false;

        const syncOrders = async () => {
            const fresh = await refreshTrackedOrderStatuses(outletId);
            if (!cancelled) {
                setTrackedOrders(fresh);
            }
        };

        setTrackedOrders(getTrackedOrders(outletId));
        syncOrders();

        const intervalId = window.setInterval(syncOrders, 15000);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [outletId]);

    const bannerHint = useMemo(() => {
        if (trackedOrders.length !== 1) return 'Tap to view status';

        const display = getTrackedOrderDisplayStatus(trackedOrders[0]);

        if (display.isCancelled) return 'Order cancelled';
        if (display.isReady) return 'Ready for pickup';

        return 'Preparing your order';
    }, [trackedOrders]);

    if (!outletId || trackedOrders.length === 0) {
        return null;
    }

    const handleClick = () => {
        if (trackedOrders.length === 1) {
            navigate(`/${outletId}/order-status`, {
                state: {
                    razorpayOrderId: trackedOrders[0].razorpayOrderId,
                },
            });
            return;
        }

        navigate(`/${outletId}/orders`);
    };

    const title =
        trackedOrders.length === 1
            ? trackedOrders[0].orderNumber
                ? `Order #${trackedOrders[0].orderNumber}`
                : 'Your order'
            : `${trackedOrders.length} orders`;

    return (
        <Box
            component={motion.div}
            {...menuFadeUp(0.12, prefersReducedMotion, canAnimate)}
            onClick={handleClick}
            sx={{
                mx: 1.6,
                mt: 1.4,
                px: 1.4,
                py: 1.15,
                display: 'flex',
                alignItems: 'center',
                gap: 1.1,
                borderRadius: '18px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D5EAF3',
                boxShadow: '0 8px 20px rgba(23, 101, 135, 0.06)',
                cursor: 'pointer'
            }}
        >
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#E7F6FE',
                    color: '#09A5F7',
                    flexShrink: 0
                }}
            >
                <ReceiptLongRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: '#0B4A67'
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#668594'
                    }}
                >
                    {bannerHint}
                </Typography>
            </Box>
            <ChevronRightRoundedIcon sx={{ color: '#7A93A0' }} />
        </Box>
    );
}

export default TrackedOrders;
